import { IForm } from 'core-components/classes/models/IForm'
import {
  BaseFetcher,
  ICrudImplementation,
  IDataProviderFilter,
  IDataProviderSort,
  IDataSource,
  IPaging,
  IPagingFromTo,
  IQueryResult,
  Logic,
  Operator,
  TConnectorFetchFn,
  TSyncCache,
} from 'core-components/types'
import { getAppId } from 'src/core/classes/helpers/getAppId'
import { getActiveTokens } from 'src/core/classes/helpers/StorageHelper'
import { proxy } from 'src/core/classes/helpers/UrlHelper'
import { HttpCancel, HttpHeaders, Rest } from 'src/core/classes/http'
import { CrudHelper } from 'src/core/types/helpers/CrudHelper'

import { catchCreateNotification } from './helpers/notifications/catchCreateNotification'
import { catchDeleteNotification } from './helpers/notifications/catchDeleteNotification'
import { catchUpdateNotification } from './helpers/notifications/catchUpdateNotification'
import { InnerStorageOperationsService } from './innerStorageHelpers/InnerStorageOperationsService'

export class CrudInnerStorageFetcher<T, R = string | number>
  extends BaseFetcher<T>
  implements ICrudImplementation<T, R>
{
  private readonly operationsService: InnerStorageOperationsService<T, R>
  private cancelObject?: HttpCancel

  constructor(
    private keyField: string,
    private spec: { formId: number; code: string; audience?: boolean }
  ) {
    super()
    this.operationsService = new InnerStorageOperationsService<T, R>(keyField)
  }

  public create = async (formId: number | IDataSource, models: IForm[]) =>
    this.makeOperation(models, formId, 'create')

  public update = async (formId: number | IDataSource, models: IForm[]) =>
    this.makeOperation(models, formId, 'update')

  public deleteBatch = async (formId: number | IDataSource, values: number[], keyField: string) => {
    try {
      let crudHelper
      if (typeof formId === 'number') {
        crudHelper = new CrudHelper(formId, this.spec.code)
      } else {
        crudHelper = new CrudHelper(parseInt(formId.formId, 10), formId.code)
      }

      const data: { [key: string]: any }[] = values.map((entryId: number) => ({
        [keyField]: entryId,
      }))
      const result = await crudHelper.delete(data, undefined, true)
      this.operationsService.removeBatchFn(this.keyField, values)
      return result
    } catch (e: unknown) {
      catchDeleteNotification(this.getMessageFromError(e))
    }
  }

  public delete = async (
    formId: number | IDataSource,
    value: number | string,
    keyField: string
  ) => {
    try {
      let crudHelper
      if (typeof formId === 'number') {
        crudHelper = new CrudHelper(formId, this.spec.code)
      } else {
        crudHelper = new CrudHelper(parseInt(formId.formId, 10), formId.code)
      }
      const result = await crudHelper.delete([{ [keyField]: value as number }], undefined, true)
      this.operationsService.removeFn(this.keyField, value as number)
      return result
    } catch (e: unknown) {
      catchDeleteNotification(this.getMessageFromError(e))
    }
  }

  public updateItem: TSyncCache<T> = ({ model, keyField, hierarchyField }): void => {
    this.operationsService.updateItem({ model, keyField, hierarchyField })
  }

  public deleteItem: TSyncCache<T> = ({ model, keyField }): void => {
    this.operationsService.deleteItem({ model, keyField })
  }

  public readById = async (
    id: R,
    keyField: string,
    query: string,
    hierarchyField?: string,
    closeExpandedRoot?: (root: R | null) => void
  ) => {
    const itemFromStore = this.operationsService.findItemInStore(id, keyField)
    if (itemFromStore) {
      const resultItem = { items: [itemFromStore], total: 0 }
      if (!this.operationsService.isQueryExistsInDataStore(query)) {
        await this.operationsService.saveToStore(
          query,
          resultItem,
          hierarchyField,
          closeExpandedRoot
        )
      }
      return resultItem
    }
    return { items: [], total: 0 }
  }

  public export = async (
    filter: IDataProviderFilter,
    fileName?: string,
    columns?: { title: string; name: string }[]
  ) => {
    const url = proxy(`/form/data-set-provider/v3/${this.spec.formId}/${this.spec.code}/export`)
    const [accessToken] = getActiveTokens()
    const headers = new HttpHeaders()

    if (accessToken) {
      headers.add('Authorization', `Bearer ${accessToken}`)
    }

    const payload = {
      Format: 'xlsx',
      FileName: fileName || `${this.spec.formId}-${this.spec.code}-export`,
      Filter: filter,
      DeleteBlob: 'true',
    }
    if (columns) {
      payload['Columns'] = columns
    }
    const result = await Rest.post({ url, payload, headers })
    return result['Data'] || {}
  }

  public read = async (
    query: string,
    filter: IDataProviderFilter,
    sort: IDataProviderSort | IDataProviderSort[] | undefined,
    paging: IPaging | IPagingFromTo,
    keyField: string,
    fetchCallBack?: TConnectorFetchFn<T>,
    hierarchyField?: string,
    expanded: (R | null)[] = [],
    root: R | null = null,
    force: boolean = false,
    skipHierarchyFilter: boolean = false,
    orderField?: string,
    closeExpandedRoot?: (root: R | null) => void,
    audience?: boolean,
    skipTotal: boolean = false
  ): Promise<IQueryResult<T, R>> => {
    if (!force) {
      const cache = await this.tryToReadFromCache(
        query,
        filter,
        hierarchyField,
        expanded,
        root,
        skipHierarchyFilter
      )
      if (cache) {
        return cache
      }
    }
    if (this.spec) {
      const storeResult = await this.fetchFromServer(
        query,
        filter,
        sort,
        paging,
        hierarchyField,
        fetchCallBack,
        closeExpandedRoot,
        audience,
        skipTotal
      )

      if (!skipHierarchyFilter && orderField) {
        this.operationsService.groupsSort(orderField, keyField)
      }
      let withGroups
      if (!skipHierarchyFilter) {
        const exp = expanded.length ? [...expanded, root] : [root]
        withGroups = await this.operationsService.readFromStore(
          query,
          hierarchyField,
          exp,
          skipHierarchyFilter
        )
      }
      if (skipHierarchyFilter) {
        return storeResult
      }
      if (withGroups) {
        return { ...withGroups, items: withGroups.items || [], error: storeResult.error }
      }
      return {
        items: [],
        total: 0,
        error: storeResult.error,
      }
    }
    return {
      items: [],
      total: 0,
    }
  }

  public saveToStore = async (
    query: string,
    data: {
      items: T[]
      total: number
    },
    hierarchyField?: string,
    closeExpandedRoot?: (root: R | null) => void
  ) => {
    await this.operationsService.saveToStore(query, data, hierarchyField, closeExpandedRoot)
  }

  public removeQueries = () => {
    return this.operationsService.removeQueries()
  }

  public groupsSort = (sortField: string, keyField: string) => {
    return this.operationsService.groupsSort(sortField, keyField)
  }

  public getDataFromStore = (): T[] => {
    return this.operationsService.getDataFromStore()
  }

  public searchInCache(keyField: string, keyValue: string): T | undefined {
    return this.operationsService.searchInStore(keyValue, keyField)
  }

  public setAudience(value: boolean) {
    if (this.spec.audience !== value) {
      this.spec.audience = value
      return true
    }
    return false
  }

  public getAudience() {
    return !!this.spec.audience
  }

  public abort = () => {
    if (this.cancelObject) {
      this.cancelObject.cancel('Operation canceled by the user')
      this.cancelObject = undefined
    }
  }

  private async makeOperation(
    items: IForm[],
    formId: number | IDataSource,
    action: 'update' | 'create' = 'update'
  ): Promise<T[]> {
    try {
      const isDataSource = typeof formId !== 'number'
      const form = isDataSource ? parseInt(formId.formId, 10) : formId
      const code = isDataSource ? formId.code : this.spec.code
      const crudHelper = new CrudHelper(form, code)

      return await crudHelper[action](
        items.map((el) => ({
          ...el,
          EntryId: el.EntryId || 0,
          AppId: el.AppId ? el.AppId : getAppId(),
        })),
        undefined,
        true
      )
    } catch (e: unknown) {
      const message = this.getMessageFromError(e)
      if (action === 'update') {
        catchUpdateNotification(message)
      } else if (action === 'create') {
        catchCreateNotification(message)
      }
      return []
    }
  }

  private tryToReadFromCache = async (
    query: string,
    filter: IDataProviderFilter,
    hierarchyField?: string,
    expanded: (R | null)[] = [],
    root: R | null = null,
    skipHierarchyFilter: boolean = false
  ): Promise<{
    items: T[]
    total: number
    groups?: T[][]
    unknownRoots?: (R | null)[]
  } | null> => {
    const cache = await this.operationsService.readFromStore(
      query,
      hierarchyField,
      expanded ? [...expanded, root || null] : [root || null],
      skipHierarchyFilter
    )
    if (cache) {
      if (cache.unknownRoots) {
        if (hierarchyField) {
          if (!cache.unknownRoots.length) {
            return { ...cache, items: cache.items || [] }
          }
          filter.filters.push({
            logic: Logic.OR,
            filters: [
              ...cache.unknownRoots.map((value) => {
                return {
                  value: value as string,
                  field: hierarchyField,
                  operator: value === null ? Operator.ISEMPTY : Operator.EQUAL,
                }
              }),
            ],
          })
        } else {
          return { ...cache, items: cache.items || [] }
        }
      } else if (cache.items) {
        return { ...cache, items: cache.items || [] }
      }
    }
    return null
  }

  private fetchFromServer = async (
    query: string,
    filter: IDataProviderFilter,
    sort: IDataProviderSort | IDataProviderSort[] | undefined,
    paging: IPaging | IPagingFromTo,
    hierarchyField?: string,
    fetchCallBack?: TConnectorFetchFn<T>,
    closeExpandedRoot?: (root: R | null) => void,
    audience?: boolean,
    skipTotal: boolean = false
  ) => {
    const crudHelper = new CrudHelper(this.spec.formId, this.spec.code)
    const newPaging = paging as IPagingFromTo
    // const CancelToken = axios.CancelToken
    // const source = CancelToken.source()
    this.cancelObject = new HttpCancel()

    const crudResult: { Data: T[]; Errors: string } = await crudHelper.readFromTo(
      filter,
      sort,
      newPaging.from,
      newPaging.to,
      this.cancelObject,
      audience,
      undefined,
      undefined,
      skipTotal
    )
    let result: T[] = crudResult.Data
    const error = crudResult.Errors || ''
    if (fetchCallBack) {
      result = (await fetchCallBack(result)) as T[]
    }

    const storeResult = {
      items: result as T[],
      total: 0,
      error,
    }

    if (!error) {
      await this.operationsService.saveToStore(
        query,
        storeResult,
        hierarchyField,
        closeExpandedRoot
      )
    }

    return storeResult
  }

  public clone(): CrudInnerStorageFetcher<T, R> {
    return new CrudInnerStorageFetcher(this.keyField, this.spec)
  }
}
